import type { ClinicalActType, EvolutionIndicationItem } from './evolutionWorkflowTypes';

/** Indicações cirúrgicas / de procedimento — oftalmologia (grade OD/OE/AO por item). */

export interface OphthalmologyIndicationItem {
  key: string;
  label: string;
  /** Natureza do ato para desfecho automático e comissão quando o cadastro não resolve. */
  act_type: ClinicalActType;
  /** Rótulo da categoria padrão do sistema (Cirurgia / Procedimento…). */
  category_name: string;
}

const CAT = {
  surgery: 'Cirurgia',
  procedureSurgical: 'Procedimento Cirúrgico',
  procedureClinical: 'Procedimento Clínico',
} as const;

export interface OphthalmologyIndicationGroup {
  title: string;
  items: OphthalmologyIndicationItem[];
}

export const OPHTHALMOLOGY_INDICATION_GROUPS: OphthalmologyIndicationGroup[] = [
  {
    title: 'LENTES DE CONTATO',
    items: [
      { key: 'ind_lc_gelatinosa', label: 'Gelatinosa', act_type: 'procedure', category_name: CAT.procedureClinical },
      { key: 'ind_lc_rigida_corneana', label: 'Rígida Corneana', act_type: 'procedure', category_name: CAT.procedureClinical },
      { key: 'ind_lc_rigida_escleral', label: 'Rígida Escleral', act_type: 'procedure', category_name: CAT.procedureClinical },
    ],
  },
  {
    title: 'LASER',
    items: [
      { key: 'ind_laser_argonio_panfoto', label: 'Argônio — Panfoto', act_type: 'procedure', category_name: CAT.procedureSurgical },
      { key: 'ind_laser_argonio_foto_focal', label: 'Argônio — Foto focal', act_type: 'procedure', category_name: CAT.procedureSurgical },
      { key: 'ind_laser_yag_capsulotomia', label: 'YAG laser — Capsulotomia', act_type: 'procedure', category_name: CAT.procedureSurgical },
      { key: 'ind_laser_yag_iridotomia', label: 'YAG laser — Iridotomia', act_type: 'procedure', category_name: CAT.procedureSurgical },
    ],
  },
  {
    title: 'CATARATA',
    items: [
      { key: 'ind_cat_convencional', label: 'Catarata convencional', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cat_complexa', label: 'Catarata complexa', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cat_congenita', label: 'Catarata congênita', act_type: 'surgery', category_name: CAT.surgery },
    ],
  },
  {
    title: 'CONJUNTIVA E PELE',
    items: [
      { key: 'ind_cp_pterigio', label: 'Pterígio', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cp_calazio', label: 'Calázio', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cp_exerese_lesoes', label: 'Exérese de lesões', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cp_exerese_bolsao', label: 'Exérese de bolsão lipídico', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cp_epilacao_cilios', label: 'Epilação de cílios', act_type: 'procedure', category_name: CAT.procedureSurgical },
    ],
  },
  {
    title: 'GLAUCOMA',
    items: [
      { key: 'ind_gl_trabeculectomia', label: 'Trabeculectomia', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_gl_agulhamento', label: 'Agulhamento', act_type: 'procedure', category_name: CAT.procedureSurgical },
      { key: 'ind_gl_valvula', label: 'Válvula filtrante', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_gl_ciclofoto', label: 'Ciclofotocoagulação', act_type: 'procedure', category_name: CAT.procedureSurgical },
    ],
  },
  {
    title: 'RETINA',
    items: [
      { key: 'ind_ret_vvpp', label: 'VVPP', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_ret_faixa_buckle', label: 'Faixa e Buckle', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_ret_retirada_oleo', label: 'Retirada de óleo', act_type: 'surgery', category_name: CAT.surgery },
    ],
  },
  {
    title: 'INJEÇÕES INTRAVÍTREAS',
    items: [
      { key: 'ind_ivt_avt', label: 'AVT', act_type: 'procedure', category_name: CAT.procedureSurgical },
      { key: 'ind_ivt_eylia', label: 'Eylia', act_type: 'procedure', category_name: CAT.procedureSurgical },
    ],
  },
  {
    title: 'PLÁSTICA',
    items: [
      { key: 'ind_plast_blef_sup', label: 'Blefaroplastia superior', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_plast_blef_inf', label: 'Blefaroplastia inferior', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_plast_ptose', label: 'Ptose', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_plast_entropio', label: 'Entrópio', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_plast_ectropio', label: 'Ectrópio', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_plast_evisceracao', label: 'Evisceração / Enucleação', act_type: 'surgery', category_name: CAT.surgery },
    ],
  },
  {
    title: 'CÓRNEA',
    items: [
      { key: 'ind_cor_refrativa', label: 'Refrativa', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cor_crosslinking', label: 'Crosslinking', act_type: 'procedure', category_name: CAT.procedureSurgical },
      { key: 'ind_cor_transplante', label: 'Transplante', act_type: 'surgery', category_name: CAT.surgery },
      { key: 'ind_cor_anel_intraestromal', label: 'Anel intraestromal', act_type: 'procedure', category_name: CAT.procedureSurgical },
    ],
  },
];

export type OphthalmologyIndicationSelection = Record<
  string,
  { od: boolean; oe: boolean; ao: boolean }
>;

const allIndicationKeys: string[] = OPHTHALMOLOGY_INDICATION_GROUPS.flatMap((g) =>
  g.items.map((i) => i.key),
);

/** Mapa label (sem lateralidade) → classificação embutida da grade oftalmológica. */
const OPHTHALMOLOGY_ITEM_BY_LABEL = new Map<string, OphthalmologyIndicationItem>(
  OPHTHALMOLOGY_INDICATION_GROUPS.flatMap((g) => g.items).map((item) => [
    item.label.trim().toLowerCase(),
    item,
  ]),
);

/** Classificação embutida da grade — não depende do cadastro da clínica. */
export function ophthalmologyEmbeddedClassification(label: string): Partial<EvolutionIndicationItem> | null {
  const row = OPHTHALMOLOGY_ITEM_BY_LABEL.get(label.trim().toLowerCase());
  if (!row) return null;
  return {
    act_type: row.act_type,
    category_name: row.category_name,
    source: 'specialty_grid',
  };
}

/** Itens estruturados a partir das marcações OD/OE/AO da grade. */
export function buildOphthalmologyIndicationItemsFromSelections(
  selections: OphthalmologyIndicationSelection,
): EvolutionIndicationItem[] {
  const out: EvolutionIndicationItem[] = [];
  for (const g of OPHTHALMOLOGY_INDICATION_GROUPS) {
    for (const row of g.items) {
      const s = selections[row.key];
      if (!s || (!s.od && !s.oe && !s.ao)) continue;
      const eyes = formatOphthalmologyIndicationEyes(s);
      if (!eyes) continue;
      out.push({
        name: `${row.label} (${eyes})`,
        act_type: row.act_type,
        category_name: row.category_name,
        source: 'specialty_grid',
      });
    }
  }
  return out;
}

export function emptyOphthalmologyIndicationSelections(): OphthalmologyIndicationSelection {
  return Object.fromEntries(allIndicationKeys.map((k) => [k, { od: false, oe: false, ao: false }]));
}

/** Lateralidade legível — AO substitui OD/OE; senão lista OD/OE marcados. */
export function formatOphthalmologyIndicationEyes(s: { od: boolean; oe: boolean; ao: boolean }): string {
  if (s.ao) return 'AO';
  const eyes: string[] = [];
  if (s.od) eyes.push('OD');
  if (s.oe) eyes.push('OE');
  return eyes.join(' / ');
}

/** Rótulos para `procedures_performed` / chips da evolução. */
export function indicationWorkflowLabelsFromSelections(
  selections: OphthalmologyIndicationSelection,
): string[] {
  const out: string[] = [];
  for (const g of OPHTHALMOLOGY_INDICATION_GROUPS) {
    for (const row of g.items) {
      const s = selections[row.key];
      if (!s || (!s.od && !s.oe && !s.ao)) continue;
      const eyes = formatOphthalmologyIndicationEyes(s);
      if (!eyes) continue;
      out.push(`${row.label} (${eyes})`);
    }
  }
  return out;
}

export function buildOphthalmologyIndicationsBody(
  selections: OphthalmologyIndicationSelection,
  outrosText: string,
  observacoes: string,
): string {
  const lines: string[] = ['INDICAÇÕES — OFTALMOLOGIA', ''];

  let n = 0;
  for (const g of OPHTHALMOLOGY_INDICATION_GROUPS) {
    for (const row of g.items) {
      const s = selections[row.key];
      if (!s || (!s.od && !s.oe && !s.ao)) continue;
      const eyes = formatOphthalmologyIndicationEyes(s);
      if (!eyes) continue;
      n += 1;
      lines.push(`${n}. ${row.label} (${eyes})`);
    }
  }

  const outros = outrosText
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  for (const item of outros) {
    n += 1;
    lines.push(`${n}. ${item}`);
  }

  if (observacoes.trim()) {
    lines.push('', 'Observações:', observacoes.trim());
  }

  lines.push('', 'Legenda: OD = olho direito · OE = olho esquerdo · AO = ambos os olhos');

  return lines.join('\n').trim();
}
